import React, { useContext, useState, useRef   } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  FormFeedback,
} from "reactstrap";
import PixwikLogo from "../../assets/pixwik/logo.png";
import { useFormik } from "formik";
import * as Yup from "yup";

import AuthenticationApi from "Api/AuthenticationApi";
import UserContext from "ContextProvider/UserContext";
import { Toast } from "primereact/toast";
import "react-toastify/dist/ReactToastify.css";

const ForgetPasswordPage = () => {
  document.title = "Forget Password";

  const [pageState, setPageState] = useState("email");
  const [otpDialog, setOtpDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const navigate = useNavigate();
  const { setUserData } = useContext(UserContext);

  const toast = useRef(null);

  const validation = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Invalid email format")
        .required("Please Enter Your Email"),
    }),
    onSubmit: (values) => {
      handleForgotPassword(values.email);
    },
  });

  const otpValidation = useFormik({
    initialValues: {
      otp: "",
    },
    validationSchema: Yup.object({
      otp: Yup.string().required("Please Enter Your OTP"),
    }),
    onSubmit: (values) => {
      handleForgotOtpVerification(userEmail, values.otp);
    },
  });

  const resetPasswordValidation = useFormik({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      newPassword: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("Please enter a new password"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword")], "Passwords do not match")
        .required("Please confirm your password"),
    }),
    onSubmit: (values) => {
      handleResetPassword(userEmail, values.newPassword);
    },
  });

  const handleForgotPassword = (email) => {
    setLoading(true);
    const payload = { email };

    AuthenticationApi.forgotPassword(payload)
      .then(() => {
        setLoading(false);
        setUserEmail(email); // Store the email for subsequent steps
        setOtpDialog(true);
        validation.resetForm();
        toast.current.show({severity:'success', summary: 'Success', detail: 'OTP sent to email', life: 3000});
      })
      .catch(() => {
        setLoading(false);
        toast.current.show({severity:'error', summary: 'Error', detail: 'Enter a valid email', life: 3000});
      });
  };

  const handleForgotOtpVerification = (email, otp) => {
    setOtpLoading(true);
    const payload = { email, otp };

    AuthenticationApi.verifyForgotOtp(payload)
      .then((res) => {
        setOtpLoading(false);
        if (res.data.status) {
          toast.current.show({severity:'success', summary: 'Success', detail: 'OTP Verified Successfully', life: 3000});
          otpValidation.resetForm();
          setPageState("reset");
        } else {
          toast.current.show({severity:'error', summary: 'Error', detail: res.data.message || "OTP verification failed", life: 3000});
        }
      })
      .catch(() => {
        setOtpLoading(false);
        toast.current.show({severity:'error', summary: 'Error', detail: 'OTP verification failed.', life: 3000});
      });
  };

  const handleResetPassword = (email, newPassword) => {
    setResetLoading(true);
    const payload = { email, password:newPassword };

    AuthenticationApi.resetPassword(payload)
      .then(() => {
        setResetLoading(false);
        toast.current.show({severity:'success', summary: 'Success', detail: 'Password reset successfully', life: 3000});
        navigate("/login");
      })
      .catch((err) => {
        setResetLoading(false);
        toast.current.show({severity:'error', summary: 'Error', detail: 'Password reset failed: ' + (err.response?.data?.message || ""), life: 3000});
      });
  };

  return (
    <React.Fragment>
      <div
        className="account-pages login-page d-flex align-items-center justify-content-center"
        style={{ height: "100vh" }}
      >
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="overflow-hidden">
                <CardBody className="pt-0 login-card">
                  <h3 className="text-center mt-5 mb-2">
                    <img
                      src={PixwikLogo}
                      alt="Pixwik Logo"
                      height="30"
                      className="auth-logo-light"
                    />
                  </h3>
                  {pageState === "reset" ? (
                    <div className="p-3">
                      <h4 className="text-muted font-size-18 mb-3 text-center">
                        Reset Password
                      </h4>

                      <form
                        className="form-horizontal mt-4"
                        onSubmit={(e) => {
                          e.preventDefault();
                          resetPasswordValidation.handleSubmit();
                        }}
                      >
                        <div className="mb-3">
                          <Label htmlFor="newPassword">New Password</Label>
                          <Input
                            name="newPassword"
                            className="form-control"
                            placeholder="Enter new password"
                            type="password"
                            onChange={resetPasswordValidation.handleChange}
                            onBlur={resetPasswordValidation.handleBlur}
                            value={resetPasswordValidation.values.newPassword || ""}
                            invalid={
                              resetPasswordValidation.touched.newPassword &&
                              resetPasswordValidation.errors.newPassword
                            }
                          />
                          {resetPasswordValidation.touched.newPassword &&
                          resetPasswordValidation.errors.newPassword ? (
                            <FormFeedback type="invalid">
                              {resetPasswordValidation.errors.newPassword}
                            </FormFeedback>
                          ) : null}
                        </div>

                        <div className="mb-3">
                          <Label htmlFor="confirmPassword">Confirm Password</Label>
                          <Input
                            name="confirmPassword"
                            className="form-control"
                            placeholder="Confirm password"
                            type="password"
                            onChange={resetPasswordValidation.handleChange}
                            onBlur={resetPasswordValidation.handleBlur}
                            value={resetPasswordValidation.values.confirmPassword || ""}
                            invalid={
                              resetPasswordValidation.touched.confirmPassword &&
                              resetPasswordValidation.errors.confirmPassword
                            }
                          />
                          {resetPasswordValidation.touched.confirmPassword &&
                          resetPasswordValidation.errors.confirmPassword ? (
                            <FormFeedback type="invalid">
                              {resetPasswordValidation.errors.confirmPassword}
                            </FormFeedback>
                          ) : null}
                        </div>

                        <Row className="mb-3">
                          <div className="col-12 text-end">
                            <button
                              className="btn btn-primary w-md waves-effect waves-light"
                              type="submit"
                              disabled={resetLoading}
                            >
                              {resetLoading ? "Resetting..." : "Reset Password"}
                            </button>
                          </div>
                        </Row>
                      </form>
                    </div>
                  ) : otpDialog ? (
                    <div className="px-3 py-3">
                      <h4 className="text-muted font-size-18 mb-1 text-center">
                        OTP Verification
                      </h4>
                      <p className="text-muted text-center">
                        Enter the OTP sent to your email {userEmail}
                      </p>

                      <form
                        className="form-horizontal mt-2"
                        onSubmit={(e) => {
                          e.preventDefault();
                          otpValidation.handleSubmit();
                        }}
                      >
                        <div className="mb-3">
                          <Label htmlFor="otp">OTP</Label>
                          <Input
                            name="otp"
                            type="text"
                            className="form-control"
                            placeholder="Enter OTP"
                            onChange={otpValidation.handleChange}
                            onBlur={otpValidation.handleBlur}
                            value={otpValidation.values.otp || ""}
                            invalid={
                              otpValidation.touched.otp && otpValidation.errors.otp
                            }
                          />
                          {otpValidation.touched.otp && otpValidation.errors.otp ? (
                            <FormFeedback type="invalid">
                              {otpValidation.errors.otp}
                            </FormFeedback>
                          ) : null}
                        </div>

                        <Row className="mb-3">
                          <div className="col-12 text-end">
                            <button
                              className="btn btn-primary w-md waves-effect waves-light"
                              type="submit"
                              disabled={otpLoading}
                            >
                              {otpLoading ? "Verifying..." : "Verify OTP"}
                            </button>
                          </div>
                        </Row>
                      </form>
                    </div>
                  ) : (
                    <div className="p-3">
                      <h4 className="text-muted font-size-18 mb-3 text-center">
                        Reset Password
                      </h4>

                      <form
                        className="form-horizontal mt-4"
                        onSubmit={(e) => {
                          e.preventDefault();
                          validation.handleSubmit();
                        }}
                      >
                        <div className="mb-3">
                          <Label htmlFor="email">Email</Label>
                          <Input
                            name="email"
                            className="form-control"
                            placeholder="Enter email"
                            type="email"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.email || ""}
                            invalid={
                              validation.touched.email && validation.errors.email
                            }
                          />
                          {validation.touched.email && validation.errors.email ? (
                            <FormFeedback type="invalid">
                              {validation.errors.email}
                            </FormFeedback>
                          ) : null}
                        </div>

                        <Row className="mb-3">
                          <div className="col-12 text-end">
                            <button
                              className="btn btn-primary w-md waves-effect waves-light"
                              type="submit"
                              disabled={loading}
                            >
                              {loading ? "Sending..." : "Reset"}
                            </button>
                          </div>
                        </Row>
                      </form>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <Toast ref={toast} />
    </React.Fragment>
  );
};

ForgetPasswordPage.propTypes = {
  history: PropTypes.object,
};

export default ForgetPasswordPage;
